import React, { useState } from 'react';
import { formatDate } from '@/lib/dateUtils';
import { 
  ArrowLeft, Plus, Search, FileText, Download, Trash2, Upload, File, 
  Eye, FolderOpen, Gavel, Clock, CheckCircle2, Users, Calendar,
  MoreVertical, Send, Award, BarChart3, Building2, Sparkles, X,
  Zap, FileCheck, DollarSign, HelpCircle, ArrowRight, CheckSquare,
  UserPlus, FileSpreadsheet, Landmark, Edit2, ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Sample demo data
const demoBidRequests = [
  { id: '1', title: 'Electrical Work - Main Panel & Wiring', project: { name: 'New Build 38th', address: '1615 38th St SE' }, trade_type: 'electrical', due_date: '2026-02-15', response_count: 2, lowest_bid: 19060, highest_bid: 21900, status: 'open', contractor_ids: ['1', '2'] },
  { id: '2', title: 'Framing - Complete Structure', project: { name: 'Riverside Residence', address: '422 River Rd' }, trade_type: 'framing', due_date: '2026-02-20', response_count: 3, lowest_bid: 48500, highest_bid: 52000, status: 'open', contractor_ids: ['3', '4', '5'] },
  { id: '3', title: 'HVAC Installation', project: { name: 'Downtown Lofts', address: '100 Main St' }, trade_type: 'hvac', due_date: '2026-02-10', response_count: 1, lowest_bid: 28000, highest_bid: 28000, status: 'awarded', contractor_ids: ['6'] },
];

const demoBidSheets = [
  { id: '1', name: 'Riverside Residence - Phase 1', property_address: '422 River Rd', status: 'approved', total_cost: 185000, bank_submission_date: '2026-01-20', approved_amount: 185000 },
  { id: '2', name: '38th Street New Build', property_address: '1615 38th St SE', status: 'submitted', total_cost: 142500, bank_submission_date: '2026-01-25' },
  { id: '3', name: 'Downtown Lofts Renovation', property_address: '100 Main St', status: 'draft', total_cost: 95000 },
];

const demoContractors = [
  { id: '1', company: 'CHL Construction', name: 'Fabian Garcia', email: 'fabian@chl.com', trade: 'Electrical', w9_received: true, ytd_paid: 45000, coi: 'project' },
  { id: '2', company: 'Block 7 Contracting', name: 'Mike Johnson', email: 'mike@block7.com', trade: 'Framing', w9_received: true, ytd_paid: 78500, coi: 'global' },
  { id: '3', company: 'Premier HVAC', name: 'Sarah Chen', email: 'sarah@premierhvac.com', trade: 'HVAC', w9_received: false, ytd_paid: 28000, coi: 'missing' },
  { id: '4', company: 'Atlas Plumbing', name: 'Tom Williams', email: 'tom@atlasplumb.com', trade: 'Plumbing', w9_received: true, ytd_paid: 52000, coi: 'project' },
];

const COI_BADGE = {
  project: { label: 'COI · project', cls: 'bg-green-100 text-green-700' },
  global: { label: 'COI · global ✓', cls: 'bg-blue-100 text-blue-700' },
  missing: { label: 'No COI', cls: 'bg-slate-100 text-slate-500' },
  requested: { label: 'COI requested', cls: 'bg-amber-100 text-amber-700' },
};

function ProjectHubDemo() {
  const [activeTab, setActiveTab] = useState('bids');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTrade, setSelectedTrade] = useState('all');
  const [expandedContractors, setExpandedContractors] = useState({});
  const [coiRequested, setCoiRequested] = useState({});

  const trades = ['All Trades', 'General', 'Framing', 'Electrical', 'Plumbing', 'HVAC', 'Concrete', 'Roofing', 'Drywall'];

  const mainTabs = [
    { id: 'bids', label: 'Bid Requests', icon: Gavel },
    { id: 'bidsheets', label: 'Bid Sheets', icon: FileSpreadsheet },
    { id: 'contractors', label: 'Contractors', icon: Users },
    { id: 'documents', label: 'Documents', icon: FolderOpen }
  ];

  const moreTabs = [
    { id: 'aiextract', label: 'AI Extract', icon: Sparkles },
    { id: 'bankformats', label: 'Bank Formats', icon: Landmark },
    { id: 'compare', label: 'Compare', icon: BarChart3 },
    { id: 'howto', label: 'How it Works', icon: HelpCircle }
  ];

  const allTabs = [...mainTabs, ...moreTabs];
  const currentTab = allTabs.find(t => t.id === activeTab);
  const isMoreTabActive = moreTabs.some(t => t.id === activeTab);

  const getTradeBadgeColor = (trade) => {
    const colors = { electrical: 'bg-yellow-100 text-yellow-700', framing: 'bg-orange-100 text-orange-700', hvac: 'bg-blue-100 text-blue-700', plumbing: 'bg-cyan-100 text-cyan-700' };
    return colors[trade?.toLowerCase()] || 'bg-purple-100 text-purple-700';
  };

  const filteredContractors = demoContractors.filter(c => {
    if (searchTerm && !c.company?.toLowerCase().includes(searchTerm.toLowerCase()) && !c.name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (selectedTrade !== 'all' && selectedTrade !== 'All Trades' && c.trade?.toLowerCase() !== selectedTrade.toLowerCase()) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4 text-center text-sm">
        <Sparkles className="w-4 h-4 inline mr-2" />
        <strong>Demo Mode</strong> — This is a preview of DrawReady's Project Hub. <a href="https://drawready.com" className="underline hover:no-underline">Sign up for full access →</a>
      </div>

      {/* Navigation */}
      <nav className="bg-white border-b shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">D</span>
              </div>
              <span className="font-bold text-xl text-slate-900">Project Hub</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center">
              <div className="flex items-center bg-slate-100 rounded-lg p-1">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      activeTab === tab.id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      isMoreTabActive ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}>
                      {isMoreTabActive && currentTab ? (
                        <><currentTab.icon className="w-4 h-4" />{currentTab.label}</>
                      ) : (
                        <><MoreVertical className="w-4 h-4" />More</>
                      )}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {moreTabs.map(tab => (
                      <DropdownMenuItem key={tab.id} onClick={() => setActiveTab(tab.id)} className={activeTab === tab.id ? 'bg-blue-50 text-blue-600' : ''}>
                        <tab.icon className="w-4 h-4 mr-2" />{tab.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => window.location.href = 'https://drawready.com'}>
              Get Started
            </Button>
          </div>
          
          {/* Mobile tabs */}
          <div className="lg:hidden pb-3 overflow-x-auto scrollbar-hide">
            <div className="flex gap-1 min-w-max">
              {allTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />{tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* ==================== BID REQUESTS TAB ==================== */}
      {activeTab === 'bids' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><Gavel className="w-7 h-7" /> Bid Requests</h1>
                  <p className="text-blue-100 mt-1">Send bid requests to contractors and compare responses</p>
                </div>
                <div className="flex gap-2">
                  <Button className="bg-white text-blue-600 hover:bg-blue-50"><Plus className="w-4 h-4 mr-2" /> New Bid Request</Button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-white"><CardContent className="p-4"><p className="text-sm text-slate-500">Open Requests</p><p className="text-2xl font-bold text-slate-800">2</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4"><p className="text-sm text-slate-500">Bids Received</p><p className="text-2xl font-bold text-green-600">6</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4"><p className="text-sm text-slate-500">Ready for Import</p><p className="text-2xl font-bold text-blue-600">2</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4"><p className="text-sm text-slate-500">Contractors</p><p className="text-2xl font-bold text-slate-800">4</p></CardContent></Card>
            </div>

            <div className="space-y-4">
              {demoBidRequests.map(bid => (
                <Card key={bid.id} className="bg-white hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getTradeBadgeColor(bid.trade_type)}`}>
                          <Gavel className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-semibold text-lg">{bid.title}</h3>
                            <Badge className="bg-blue-100 text-blue-700">{bid.response_count} BIDS</Badge>
                            {bid.response_count > 0 && bid.status !== 'awarded' && <Badge className="bg-green-100 text-green-700">Ready for Import</Badge>}
                            {bid.status === 'awarded' && <Badge className="bg-purple-100 text-purple-700"><Award className="w-3 h-3 mr-1" /> Awarded</Badge>}
                          </div>
                          <p className="text-slate-500 text-sm mb-2">{bid.project?.name} - {bid.project?.address}</p>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span><Calendar className="w-4 h-4 inline mr-1" /> Due: {formatDate(bid.due_date)}</span>
                            <span><Users className="w-4 h-4 inline mr-1" /> {bid.contractor_ids?.length} contractors</span>
                          </div>
                          {bid.lowest_bid && (
                            <div className="flex items-center gap-3 mt-3">
                              <span className="text-sm text-slate-500">Lowest:</span>
                              <span className="text-lg font-bold text-green-600">${bid.lowest_bid.toLocaleString()}</span>
                              {bid.highest_bid !== bid.lowest_bid && (
                                <span className="text-sm text-slate-400">Range: ${(bid.lowest_bid/1000).toFixed(1)}K - ${(bid.highest_bid/1000).toFixed(1)}K</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {bid.response_count > 0 && bid.status !== 'awarded' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700"><FileSpreadsheet className="w-4 h-4 mr-2" /> Import to Bid Sheet</Button>
                        )}
                        <Button size="sm" variant="outline"><BarChart3 className="w-4 h-4 mr-2" /> Compare</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ==================== BID SHEETS TAB ==================== */}
      {activeTab === 'bidsheets' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><FileSpreadsheet className="w-7 h-7" /> Bid Sheets</h1>
                  <p className="text-blue-100 mt-1">Create bid sheets from received bids to submit to the bank</p>
                </div>
                <Button className="bg-white text-blue-600 hover:bg-blue-50"><Plus className="w-4 h-4 mr-2" /> New Bid Sheet</Button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            <Alert className="bg-blue-50 border-blue-200 mb-6">
              <HelpCircle className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <div className="flex items-center gap-2"><div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div><span>Import costs from bids</span></div>
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  <div className="flex items-center gap-2"><div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div><span>Review & finalize</span></div>
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  <div className="flex items-center gap-2"><div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div><span>Submit to bank</span></div>
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  <div className="flex items-center gap-2"><div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</div><span>Bank approves → Award contractors</span></div>
                </div>
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              {demoBidSheets.map(sheet => {
                const statusConfig = {
                  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700' },
                  submitted: { label: 'Pending Bank Approval', className: 'bg-yellow-100 text-yellow-700' },
                  approved: { label: 'Bank Approved', className: 'bg-green-100 text-green-700' }
                };
                const status = statusConfig[sheet.status] || statusConfig.draft;
                
                return (
                  <Card key={sheet.id} className={`bg-white hover:shadow-md transition-shadow ${sheet.status === 'approved' ? 'border-l-4 border-green-500' : ''}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{sheet.name}</h3>
                            <Badge className={status.className}>{sheet.status === 'approved' && <CheckCircle2 className="w-3 h-3 mr-1" />}{status.label}</Badge>
                          </div>
                          <p className="text-slate-500 text-sm mb-3">{sheet.property_address}</p>
                          <div className="flex items-center gap-6 text-sm">
                            <span className="text-slate-600"><strong>Total Budget:</strong> ${sheet.total_cost?.toLocaleString()}</span>
                            {sheet.bank_submission_date && <span className="text-slate-600"><strong>Submitted:</strong> {formatDate(sheet.bank_submission_date)}</span>}
                            {sheet.approved_amount && <span className="text-green-600"><strong>Approved:</strong> ${sheet.approved_amount.toLocaleString()}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {sheet.status === 'approved' && <Button size="sm" className="bg-green-600 hover:bg-green-700"><Award className="w-4 h-4 mr-2" /> Award Contractors</Button>}
                          {sheet.status === 'submitted' && <Button size="sm" className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark Approved</Button>}
                          {sheet.status === 'draft' && <Button size="sm" variant="outline"><Landmark className="w-4 h-4 mr-2" /> Submit to Bank</Button>}
                          <Button variant="outline" size="sm"><Eye className="w-4 h-4 mr-2" /> View</Button>
                          <Button size="sm"><Download className="w-4 h-4 mr-2" /> Export</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ==================== CONTRACTORS TAB ==================== */}
      {activeTab === 'contractors' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><Users className="w-7 h-7" /> Contractors</h1>
                  <p className="text-blue-100 mt-1">Manage contractors, payments, and 1099 reporting</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20"><FileText className="w-4 h-4 mr-2" /> 1099 Report</Button>
                  <Button className="bg-white text-blue-600 hover:bg-blue-50"><Plus className="w-4 h-4 mr-2" /> Add Contractor</Button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-white"><CardContent className="p-4"><p className="text-sm text-slate-500">Total Contractors</p><p className="text-2xl font-bold text-slate-800">4</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4"><p className="text-sm text-slate-500">With W-9</p><p className="text-2xl font-bold text-green-600">3</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4"><p className="text-sm text-slate-500">YTD Paid</p><p className="text-2xl font-bold text-slate-800">$203,500</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4"><p className="text-sm text-slate-500">1099 Required</p><p className="text-2xl font-bold text-orange-600">4</p></CardContent></Card>
            </div>

            {/* Trade Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              {trades.map(trade => (
                <button
                  key={trade}
                  onClick={() => setSelectedTrade(trade === 'All Trades' ? 'all' : trade)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    (selectedTrade === 'all' && trade === 'All Trades') || selectedTrade === trade ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {trade}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search contractors..." className="pl-10 bg-white" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>

            <div className="space-y-4">
              {filteredContractors.map(c => (
                <Card key={c.id} className="bg-white hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 ${getTradeBadgeColor(c.trade)} rounded-full flex items-center justify-center text-white font-bold`}>
                          {c.company.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold">{c.company}</h3>
                            <Badge className={getTradeBadgeColor(c.trade)}>{c.trade}</Badge>
                            <Badge className={c.w9_received ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{c.w9_received ? 'W-9 ✓' : 'Pending W-9'}</Badge>
                            {(() => { const meta = COI_BADGE[coiRequested[c.id] ? 'requested' : c.coi] || COI_BADGE.missing; return <Badge className={meta.cls} data-testid={`demo-coi-badge-${c.id}`}>{meta.label}</Badge>; })()}
                          </div>
                          <p className="text-sm text-slate-500">{c.name} • {c.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-2">
                          <p className="text-sm text-slate-500">YTD Paid</p>
                          <p className="text-lg font-semibold text-slate-800">${c.ytd_paid.toLocaleString()}</p>
                          {c.ytd_paid >= 600 && <p className="text-xs text-orange-600">1099 Required</p>}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`demo-request-coi-${c.id}`}
                          disabled={!!coiRequested[c.id]}
                          onClick={() => setCoiRequested(prev => ({ ...prev, [c.id]: true }))}
                        >
                          <Shield className="w-4 h-4 mr-1" /> {coiRequested[c.id] ? 'COI Requested' : 'Request COI'}
                        </Button>
                        <Button variant="outline" size="sm"><Edit2 className="w-4 h-4 mr-1" /> Edit</Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700"><DollarSign className="w-4 h-4 mr-1" /> Log Payment</Button>
                        <Button variant="outline" size="sm"><FileText className="w-4 h-4 mr-1" /> 1099</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ==================== DOCUMENTS TAB ==================== */}
      {activeTab === 'documents' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><FolderOpen className="w-7 h-7" /> Documents</h1>
                  <p className="text-blue-100 mt-1">Manage project documents, plans, and contractor files</p>
                </div>
                <Button className="bg-white text-blue-600 hover:bg-blue-50"><Upload className="w-4 h-4 mr-2" /> Upload</Button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            <Card className="bg-white">
              <CardContent className="py-16 text-center">
                <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Document Management</h3>
                <p className="text-slate-500 mb-6">Upload and organize plans, specs, contracts, W-9s, and more</p>
                <Button><Upload className="w-4 h-4 mr-2" /> Upload Document</Button>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* ==================== AI EXTRACT TAB ==================== */}
      {activeTab === 'aiextract' && (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl"><Sparkles className="w-6 h-6 text-purple-600" /> AI Data Extraction</CardTitle>
              <CardDescription>Upload documents and let AI extract key data for you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="bg-purple-50 border-purple-200">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <AlertDescription className="text-purple-800">Upload bid documents, contracts, or invoices and AI will automatically extract line items, costs, and key details.</AlertDescription>
              </Alert>

              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-purple-400 transition-colors cursor-pointer">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Upload Document to Extract</h3>
                <p className="text-slate-500 mb-4">Supports PDF, Excel, Word, and image files</p>
                <Button className="bg-purple-600 hover:bg-purple-700"><Upload className="w-4 h-4 mr-2" /> Choose File</Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> From Bids & Estimates</h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Line items & descriptions</li>
                    <li>• Unit prices & quantities</li>
                    <li>• Total amounts</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-600" /> From Invoices</h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Invoice number & date</li>
                    <li>• Vendor information</li>
                    <li>• Payment amounts</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== HOW IT WORKS TAB ==================== */}
      {activeTab === 'howto' && (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="bg-white">
            <CardHeader><CardTitle className="flex items-center gap-2 text-2xl"><HelpCircle className="w-6 h-6 text-blue-600" /> How the Bid Management System Works</CardTitle></CardHeader>
            <CardContent className="space-y-8">
              {[
                { step: 1, title: 'Create Bid Requests', desc: 'Send bid requests to contractors for specific scopes of work. Attach plans and specs.', color: 'blue' },
                { step: 2, title: 'Receive & Compare Bids', desc: 'Contractors submit itemized bids. Compare them side-by-side to find the best value.', color: 'blue' },
                { step: 3, title: 'Import to Bid Sheet', desc: 'Take the costs from received bids and import them into a bid sheet for bank submission.', color: 'blue' },
                { step: 4, title: 'Submit to Bank', desc: 'Export and submit your bid sheet to the bank for funding approval.', color: 'blue' },
                { step: 5, title: 'Bank Approves → Award Contractors', desc: 'Once the bank approves your budget, award the contractors. They receive an email notification.', color: 'green' }
              ].map(item => (
                <div key={item.step} className="flex gap-4">
                  <div className={`w-10 h-10 ${item.color === 'green' ? 'bg-green-100' : 'bg-blue-100'} rounded-full flex items-center justify-center flex-shrink-0`}>
                    <span className={`${item.color === 'green' ? 'text-green-600' : 'text-blue-600'} font-bold`}>{item.step}</span>
                  </div>
                  <div><h3 className="font-semibold text-lg mb-1">{item.title}</h3><p className="text-slate-600">{item.desc}</p></div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Other tabs show placeholder */}
      {(activeTab === 'bankformats' || activeTab === 'compare') && (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="bg-white">
            <CardContent className="py-16 text-center">
              {activeTab === 'bankformats' && <Landmark className="w-16 h-16 text-slate-300 mx-auto mb-4" />}
              {activeTab === 'compare' && <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />}
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                {activeTab === 'bankformats' ? 'Bank Format Templates' : 'Bid Comparison Tool'}
              </h3>
              <p className="text-slate-500">Sign up to access this feature</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ProjectHubDemo;
